import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, functions, app } from './firebaseConfig';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  OAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { initializeMessaging, requestNotificationPermission } from './notificationService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize Firebase Messaging
  useEffect(() => {
    initializeMessaging(app);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('=== AUTH STATE CHANGED ===', user?.uid);
      setCurrentUser(user);
      
      if (user && !user.isAnonymous) {
        // Fetch user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const profile = userDoc.data();
            setUserProfile(profile);
            console.log('User profile loaded:', profile);
            
            // Skip auto-update - profile should be updated via OAuth sign-in
            // The OAuth flow in signInWithGoogle/Twitch/TikTok handles profile updates
          } else {
            console.log('No user profile found');
            setUserProfile(null);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in with Google (also used for YouTube verification)
  const signInWithGoogle = async (asCreator = false, isYouTubeCreator = false) => {
    try {
      console.log('=== SIGNING IN WITH GOOGLE ===', { asCreator, isYouTubeCreator });
      const provider = new GoogleAuthProvider();
      
      // Add YouTube scope for creators to get channel data
      if (asCreator && isYouTubeCreator) {
        provider.addScope('https://www.googleapis.com/auth/youtube.readonly');
      }
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;

      // Fetch YouTube channel data if this is a YouTube creator
      let youtubeChannelData = null;
      if (asCreator && isYouTubeCreator && accessToken) {
        try {
          console.log('Fetching YouTube channel data with access token...');
          const getYouTubeData = httpsCallable(functions, 'getYouTubeChannelDataFromToken');
          const response = await getYouTubeData({ accessToken });
          youtubeChannelData = response.data;
          console.log('YouTube channel data:', youtubeChannelData);
        } catch (error) {
          console.error('Error fetching YouTube data:', error);
          console.error('Error details:', error.code, error.message, error.details);
          // Show error to user
          throw new Error('Failed to fetch YouTube channel data: ' + (error.message || 'Unknown error'));
        }
      }

      // Create or update user profile with YouTube data
      const updatedProfile = await createUserProfile(user, asCreator, youtubeChannelData);
      
      // Force update the local state with the new profile data
      setUserProfile(updatedProfile);
      
      // Request notification permission after sign in
      setTimeout(() => {
        requestNotificationPermission(user.uid);
      }, 2000); // Wait 2 seconds after sign-in to ask for notifications
      
      return user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to sign in with Google. ';
      
      switch (error.code) {
        case 'auth/operation-not-allowed':
          errorMessage += 'Google sign-in is not enabled. Please contact support.';
          break;
        case 'auth/popup-blocked':
          errorMessage += 'Popup was blocked by your browser. Please allow popups and try again.';
          break;
        case 'auth/popup-closed-by-user':
          errorMessage += 'Sign-in was cancelled.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage += 'This domain is not authorized. Please contact support.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage += 'Only one popup allowed at a time.';
          break;
        default:
          errorMessage += error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  // Sign in with Twitch (for verified creators)
  const signInWithTwitch = async () => {
    try {
      console.log('=== SIGNING IN WITH TWITCH ===');
      
      // Twitch OAuth configuration
      const twitchClientId = process.env.REACT_APP_TWITCH_CLIENT_ID;
      
      if (!twitchClientId) {
        throw new Error('Twitch sign-in is not configured yet. Please contact support or use Google sign-in.');
      }
      
      const redirectUri = encodeURIComponent(window.location.origin);
      const scopes = 'user:read:email';
      const state = Math.random().toString(36).substring(7);
      
      // Store state for verification
      sessionStorage.setItem('twitch_oauth_state', state);
      
      // Redirect to Twitch OAuth
      const twitchAuthUrl = `https://id.twitch.tv/oauth2/authorize?client_id=${twitchClientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scopes}&state=${state}&force_verify=true`;
      
      window.location.href = twitchAuthUrl;
    } catch (error) {
      console.error('Error initiating Twitch sign-in:', error);
      throw error;
    }
  };

  // Sign in with TikTok (for verified creators)
  const signInWithTikTok = async () => {
    try {
      console.log('=== SIGNING IN WITH TIKTOK ===');
      
      // TikTok OAuth configuration
      const tiktokClientKey = process.env.REACT_APP_TIKTOK_CLIENT_KEY;
      const redirectUri = encodeURIComponent(window.location.origin);
      const state = Math.random().toString(36).substring(7);
      
      // Store state for verification
      sessionStorage.setItem('tiktok_oauth_state', state);
      
      // Redirect to TikTok OAuth
      const tiktokAuthUrl = `https://www.tiktok.com/v2/auth/authorize/?client_key=${tiktokClientKey}&scope=user.info.basic&response_type=code&redirect_uri=${redirectUri}&state=${state}`;
      
      window.location.href = tiktokAuthUrl;
    } catch (error) {
      console.error('Error initiating TikTok sign-in:', error);
      throw error;
    }
  };

  // Sign up with Email/Password
  const signUpWithEmail = async (email, password, asCreator = false) => {
    try {
      console.log('=== SIGNING UP WITH EMAIL ===', { email, asCreator });
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;

      // Send email verification
      await sendEmailVerification(user);

      // Create user profile
      await createUserProfile(user, asCreator);
      
      // Request notification permission after sign in
      setTimeout(() => {
        requestNotificationPermission(user.uid);
      }, 2000);
      
      return user;
    } catch (error) {
      console.error('Error signing up with email:', error);
      
      let errorMessage = 'Failed to create account. ';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage += 'This email is already registered. Please sign in instead.';
          break;
        case 'auth/invalid-email':
          errorMessage += 'Invalid email address.';
          break;
        case 'auth/weak-password':
          errorMessage += 'Password should be at least 6 characters.';
          break;
        default:
          errorMessage += error.message;
      }
      
      error.message = errorMessage;
      throw error;
    }
  };

  // Sign in with Email/Password
  const signInWithEmail = async (email, password) => {
    try {
      console.log('=== SIGNING IN WITH EMAIL ===', { email });
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      // Request notification permission after sign in
      setTimeout(() => {
        requestNotificationPermission(user.uid);
      }, 2000);
      
      return user;
    } catch (error) {
      console.error('Error signing in with email:', error);
      
      let errorMessage = 'Failed to sign in. ';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage += 'No account found with this email.';
          break;
        case 'auth/wrong-password':
          errorMessage += 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMessage += 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage += 'This account has been disabled.';
          break;
        default:
          errorMessage += error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  // Create or update user profile in Firestore
  const createUserProfile = async (user, isCreator = false, platformData = null) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const existingData = userDoc.exists() ? userDoc.data() : null;

      // If user is signing in as creator, upgrade them to creator
      // Otherwise, keep existing account type or set as player for new users
      const accountType = isCreator ? 'creator' : (existingData?.accountType || 'player');

      const profileData = {
        uid: user.uid,
        email: user.email,
        displayName: existingData?.displayName || platformData?.displayName || user.displayName || 'Anonymous',
        photoURL: existingData?.photoURL || platformData?.photoURL || user.photoURL || '',
        accountType: accountType,
        isCreator: isCreator || existingData?.isCreator || false, // Upgrade if signing in as creator
        createdAt: existingData?.createdAt || new Date(),
        lastLogin: new Date(),
        totalPointsEarned: existingData?.totalPointsEarned || 0,
        gamesPlayed: existingData?.gamesPlayed || 0,
        gamesWon: existingData?.gamesWon || 0,
      };

      // Additional fields for creators
      if (isCreator) {
        profileData.creatorProfile = {
          channelUrl: platformData?.channelUrl || existingData?.creatorProfile?.channelUrl || '',
          promotionalUrl: platformData?.channelUrl || existingData?.creatorProfile?.promotionalUrl || '',
          platform: platformData ? 'YouTube' : (existingData?.creatorProfile?.platform || ''),
          contentType: existingData?.creatorProfile?.contentType || '',
          bio: existingData?.creatorProfile?.bio || '',
          referralClicks: existingData?.creatorProfile?.referralClicks || 0,
          profileComplete: platformData ? true : (existingData?.creatorProfile?.profileComplete || false),
          // Platform verification fields
          platformVerified: platformData?.verified || (existingData?.creatorProfile?.platformVerified || false),
          verifiedPlatform: platformData ? 'youtube' : (existingData?.creatorProfile?.verifiedPlatform || null),
          verifiedAt: platformData ? new Date().toISOString() : (existingData?.creatorProfile?.verifiedAt || null),
        };
      }

      await setDoc(userRef, profileData, { merge: true });
      setUserProfile(profileData);
      
      console.log('User profile created/updated:', profileData);
      return profileData;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      console.log('=== SIGNING OUT ===');
      setCurrentUser(null);
      setUserProfile(null);
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Fetch YouTube channel data and update profile
  const updateProfileFromYouTube = async (userId, channelUrl) => {
    try {
      // Call Cloud Function to get YouTube data (keeps API key secure)
      const getYouTubeData = httpsCallable(functions, 'getYouTubeChannelData');
      const result = await getYouTubeData({ channelUrl });

      if (result.data.success) {
        const { displayName, photoURL } = result.data;

        // Update Firestore profile
        const userRef = doc(db, 'users', userId);
        await setDoc(userRef, {
          displayName: displayName,
          photoURL: photoURL,
        }, { merge: true });

        // Update local state
        setUserProfile(prev => ({
          ...prev,
          displayName: displayName,
          photoURL: photoURL,
        }));

        console.log('Updated profile from YouTube:', { displayName, photoURL });
      }
    } catch (error) {
      console.error('Error updating profile from YouTube:', error);
    }
  };

  // Fetch Twitch channel data and update profile
  const updateProfileFromTwitch = async (userId, channelUrl) => {
    try {
      // Call Cloud Function to get Twitch data (keeps client secret secure)
      const getTwitchData = httpsCallable(functions, 'getTwitchChannelData');
      const result = await getTwitchData({ channelUrl });

      if (result.data.success) {
        const { displayName, photoURL } = result.data;

        // Update Firestore profile
        const userRef = doc(db, 'users', userId);
        await setDoc(userRef, {
          displayName: displayName,
          photoURL: photoURL,
        }, { merge: true });

        // Update local state
        setUserProfile(prev => ({
          ...prev,
          displayName: displayName,
          photoURL: photoURL,
        }));

        console.log('Updated profile from Twitch:', { displayName, photoURL });
      }
    } catch (error) {
      console.error('Error updating profile from Twitch:', error);
    }
  };

  // Update creator profile
  const updateCreatorProfile = async (updates) => {
    if (!currentUser || (userProfile?.accountType !== 'creator' && !userProfile?.isCreator)) {
      throw new Error('Only creators can update their profile');
    }

    try {
      // Helper function to ensure URL has https://
      const ensureHttps = (url) => {
        if (!url) return url;
        const trimmedUrl = url.trim();
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
          return 'https://' + trimmedUrl;
        }
        return trimmedUrl;
      };

      // Process updates to ensure URLs have https://
      const processedUpdates = { ...updates };
      if (processedUpdates.channelUrl) {
        processedUpdates.channelUrl = ensureHttps(processedUpdates.channelUrl);
      }
      if (processedUpdates.promotionalUrl) {
        processedUpdates.promotionalUrl = ensureHttps(processedUpdates.promotionalUrl);
      }

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Also update top-level promotionalURL for backward compatibility
      const topLevelUpdates = {};
      if (processedUpdates.promotionalUrl) {
        topLevelUpdates.promotionalURL = processedUpdates.promotionalUrl;
      }
      
      await setDoc(userRef, {
        ...topLevelUpdates,
        creatorProfile: {
          ...(userProfile?.creatorProfile || {}),
          ...processedUpdates,
        }
      }, { merge: true });

      const updatedProfile = {
        ...userProfile,
        ...topLevelUpdates,
        creatorProfile: {
          ...(userProfile?.creatorProfile || {}),
          ...processedUpdates,
        }
      };
      setUserProfile(updatedProfile);
      
      console.log('Creator profile updated:', processedUpdates);
      return updatedProfile;
    } catch (error) {
      console.error('Error updating creator profile:', error);
      throw error;
    }
  };

  // Complete creator onboarding
  const completeCreatorOnboarding = async (channelUrl, promotionalUrl, platform, contentType, youtubeData = null, profileImage = null, subscriberCount = '', creatorGoal = '') => {
    if (!currentUser) {
      throw new Error('Must be signed in to complete onboarding');
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Helper function to ensure URL has https://
      const ensureHttps = (url) => {
        if (!url) return url;
        const trimmedUrl = url.trim();
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
          return 'https://' + trimmedUrl;
        }
        return trimmedUrl;
      };
      
      // Use YouTube data if available, otherwise extract from URL
      let channelName = userProfile?.displayName || currentUser.displayName || 'Creator';
      let profilePhotoURL = userProfile?.photoURL || currentUser.photoURL || '';

      // If profile image was uploaded, we'll need to upload it to storage
      if (profileImage) {
        // For now, we'll use a data URL - you may want to upload to Firebase Storage later
        const reader = new FileReader();
        const photoURLPromise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(profileImage);
        });
        profilePhotoURL = await photoURLPromise;
      } else if (youtubeData && youtubeData.photoURL) {
        profilePhotoURL = youtubeData.photoURL;
      } else if (youtubeData && youtubeData.name) {
        channelName = youtubeData.name;
      } else if (channelUrl) {
        // Simple extraction - you can make this more sophisticated
        const urlMatch = channelUrl.match(/(?:youtube\.com\/(?:c\/|channel\/|@)?|twitch\.tv\/|kick\.com\/)([^\/\?]+)/i);
        if (urlMatch) {
          channelName = urlMatch[1];
        }
      }

      // Ensure URLs have https://
      const finalChannelUrl = ensureHttps(channelUrl);
      const finalPromotionalUrl = ensureHttps(promotionalUrl || channelUrl);

      const updates = {
        displayName: channelName,
        photoURL: profilePhotoURL,
        promotionalURL: finalPromotionalUrl, // Use promotional URL with https://
        creatorProfile: {
          ...(userProfile?.creatorProfile || {}),
          channelUrl: finalChannelUrl,
          promotionalUrl: finalPromotionalUrl,
          platform: platform || '',
          contentType: contentType || '',
          subscriberCount: subscriberCount || '',
          creatorGoal: creatorGoal || '',
          profileComplete: true,
        }
      };

      await setDoc(userRef, updates, { merge: true });

      // Auto-register creator to current cycle's leaderboard
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const currentCycleId = `${year}-${month}-${day}-18:00`;

      const leaderboardRef = doc(db, 'cycles', currentCycleId, 'leaderboard', currentUser.uid);
      const leaderboardSnap = await getDoc(leaderboardRef);
      
      if (!leaderboardSnap.exists()) {
        await setDoc(leaderboardRef, {
          creatorId: currentUser.uid,
          totalPoints: 0,
          supporterCount: 0,
          supporters: [],
          firstToReachCurrentScore: Date.now(),
          lastUpdated: Date.now()
        });
      }

      const updatedProfile = {
        ...(userProfile || {}),
        ...updates,
      };
      setUserProfile(updatedProfile);
      
      console.log('Creator onboarding completed and added to leaderboard:', updates);
      return updatedProfile;
    } catch (error) {
      console.error('Error completing creator onboarding:', error);
      throw error;
    }
  };

  // Upgrade existing player account to creator
  const upgradeToCreator = async () => {
    if (!currentUser) {
      throw new Error('Must be signed in to upgrade to creator');
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        accountType: 'creator',
        isCreator: true,
      }, { merge: true });

      const updatedProfile = {
        ...userProfile,
        accountType: 'creator',
        isCreator: true,
      };
      setUserProfile(updatedProfile);
      
      console.log('Account upgraded to creator');
      return updatedProfile;
    } catch (error) {
      console.error('Error upgrading to creator:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    signInWithTwitch,
    signInWithTikTok,
    signOut,
    updateCreatorProfile,
    completeCreatorOnboarding,
    upgradeToCreator,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
